/**
 * domain-guard.ts
 *
 * Runs immediately on page boot. Asks the server whether the current hostname is a
 * known legitimate storefront. If the server says no, all page content is wiped —
 * preventing a reverse-proxy clone from rendering anything useful to the victim.
 *
 * Design principles:
 *  - Holds NO allowlist. Validation is entirely server-side.
 *  - Fails open: loopback, network errors, or parse errors all result in a normal page load.
 *  - Runs regardless of the server-side ApiOriginGuard Mode setting.
 */

const HOST_ALLOWED_PATH = '/api/v1.0/olo/host-allowed';

const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]', '::1']);

export async function checkDomain(): Promise<void> {
    const hostname = window.location.hostname;

    // Fail open on loopback / dev
    if (LOOPBACK_HOSTS.has(hostname)) return;

    try {
        const url = `${HOST_ALLOWED_PATH}?h=${encodeURIComponent(hostname)}`;
        const response = await fetch(url, {
            method: 'GET',
            credentials: 'same-origin',
            cache: 'no-store'
        });

        // Any non-2xx -> fail open (server error, maintenance, etc.)
        if (!response.ok) return;

        const data = await response.json() as { allowed?: boolean };

        if (data.allowed === false) {
            // Wipe page — no valid storefront markup is left in the DOM.
            document.documentElement.innerHTML = '';
        }
    } catch {
        // Fail open on network error, JSON parse error, or any other exception.
    }
}

// Fire-and-forget: does not block module evaluation, but starts as early as possible.
checkDomain();
